'use client';

import * as React from 'react';
import {ArrowLeft, X as CloseIcon} from 'lucide-react';
import {useResponsive} from '@purpose/ui/hooks/useResponsive';
import {Button} from '../button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogClose
} from '../dialog/dialog';
import {Typography} from '../typography';
import {cn} from '@purpose/ui/lib/utils';

interface ModalProps {
  open: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  onBack?: () => void;
  onOpenChange?: (next: boolean) => void;
  title: string | React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
  buttonTitle?: string;
  cancelButtonTitle?: string;
  destructive?: boolean;
  saveDisabled?: boolean;
  saveLoading?: boolean;
  withCloseButton?: boolean;
  saveButton?: React.ReactNode;
  leftButton?: React.ReactNode;
  t?: (key: string) => string;
  className?: string;
  dialogClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  saveButtonClassName?: string;
  topContainerClassName?: string;
  renderNextToTitleElement?: () => React.ReactNode;
}

const Modal = ({
  open,
  onClose,
  onCancel,
  onSave,
  onBack,
  onOpenChange,
  title,
  subtitle,
  children,
  buttonTitle,
  cancelButtonTitle,
  destructive = false,
  saveDisabled = false,
  saveLoading = false,
  withCloseButton = false,
  saveButton,
  leftButton,
  t,
  className,
  dialogClassName,
  titleClassName,
  subtitleClassName,
  saveButtonClassName,
  topContainerClassName,
  renderNextToTitleElement
}: ModalProps) => {
  const {isTabletOrSmaller} = useResponsive();
  return (
    <Dialog
      open={open}
      onOpenChange={open => {
        onOpenChange?.(open);
        !open && onClose?.();
      }}
    >
      <DialogContent
        className={`${
          isTabletOrSmaller
            ? `w-full max-w-[calc(100%-32px)] min-w-auto p-6`
            : 'm-8 w-auto max-w-125 min-w-125 p-6'
        } bg-secondary-10 border-secondary-20 flex flex-col gap-6 rounded-lg border ${dialogClassName || ''} ${className || ''}`}
      >
        <div
          className={cn(
            'flex w-full shrink-0 items-start justify-between gap-6',
            topContainerClassName
          )}
        >
          <div className="flex w-full items-center gap-2">
            {onBack && (
              <button
                aria-label="Tilbake"
                onClick={onBack}
                className="bg-secondary-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full"
              >
                <ArrowLeft className="text-secondary-100" />
              </button>
            )}
            <div className="flex w-full flex-col">
              <DialogTitle
                className={`${
                  isTabletOrSmaller ? 'text-2xl' : 'text-3xl'
                } text-secondary-100 py-2 font-semibold ${titleClassName || ''}`}
              >
                {title}
                {renderNextToTitleElement ? renderNextToTitleElement?.() : null}
              </DialogTitle>
              {subtitle && (
                <Typography
                  className={`pt-1 ${subtitleClassName || ''}`}
                  variant="body2"
                >
                  {subtitle}
                </Typography>
              )}
            </div>
          </div>
          {withCloseButton && (
            <DialogClose
              onClick={onCancel || onClose}
              className="bg-secondary-30 focus:ring-ring flex cursor-pointer items-center justify-center rounded p-0 focus:ring-2 focus:ring-offset-2"
            >
              <CloseIcon className="h-4 w-4" />
            </DialogClose>
          )}
        </div>

        {!!children && (
          <div className="min-h-0 w-full flex-1 overflow-y-auto">
            {children}
          </div>
        )}

        {(onSave || saveButton || onCancel) && (
          <DialogFooter className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2">
            {leftButton && leftButton}

            {onCancel && (
              <Button
                variant="secondary"
                className={`${isTabletOrSmaller ? 'w-full' : 'w-auto'} h-10 bg-[#EDEDED]`}
                onClick={onCancel}
                aria-label={
                  cancelButtonTitle || (t ? t('General.cancel') : 'Avbryt')
                }
              >
                {cancelButtonTitle || (t ? t('General.cancel') : 'Cancel')}
              </Button>
            )}

            {(onSave || saveButton) &&
              (saveButton ||
                (destructive ? (
                  <Button
                    variant="destructive"
                    className={cn(
                      `${
                        isTabletOrSmaller ? 'w-full' : 'w-auto'
                      } h-9! gap-1 rounded-[7px] px-3 text-sm font-normal`,
                      saveButtonClassName
                    )}
                    onClick={onSave}
                    disabled={saveDisabled}
                    isLoading={saveLoading}
                    aria-label={buttonTitle}
                  >
                    {buttonTitle}
                  </Button>
                ) : (
                  <Button
                    className={cn(
                      `${isTabletOrSmaller ? 'w-full' : 'w-auto'} px-3`,
                      saveButtonClassName
                    )}
                    onClick={onSave}
                    disabled={saveDisabled}
                    isLoading={saveLoading}
                    aria-label={buttonTitle}
                  >
                    {buttonTitle}
                  </Button>
                )))}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
